import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Loader2, Plus, GraduationCap, Briefcase, CheckCircle2, Circle } from "lucide-react";

const STATUSES = ["applied", "interview", "offer", "rejected"];

export default function Student() {
  const [d, setD] = useState(null);
  const [a, setA] = useState({ title: "", course: "", due_date: "" });
  const [i, setI] = useState({ company: "", role: "", status: "applied" });

  const load = useCallback(async () => { const { data } = await api.get("/student/dashboard"); setD(data); }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (fn) => { try { await fn(); await load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  if (!d) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#FF7E67]" /></div>;

  return (
    <div className="space-y-8" data-testid="student-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-sunrise">Student Edition</p>
        <h1 className="mt-2 font-nunito font-extrabold tracking-tight text-4xl text-[#2D3748] sm:text-5xl">Your AI Study Partner</h1>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-[#FFE8E3] bg-white p-6 text-center">
          <p className="font-nunito font-extrabold tracking-tight text-4xl text-sunrise">{d.readiness}</p>
          <p className="text-xs text-[#718096]">Career Readiness</p>
        </div>
        <div className="rounded-2xl border border-[#FFE8E3] bg-white p-6 text-center">
          <p className="font-nunito font-extrabold tracking-tight text-4xl text-[#2D3748]">{d.assignments.length}</p>
          <p className="text-xs text-[#718096]">Open Assignments</p>
        </div>
        <div className="rounded-2xl border border-[#FFE8E3] bg-white p-6 text-center">
          <p className="font-nunito font-extrabold tracking-tight text-4xl text-[#2D3748]">{d.internships.length}</p>
          <p className="text-xs text-[#718096]">Applications</p>
        </div>
      </div>

      <div className="rounded-3xl border border-[#FFE8E3] bg-white p-6">
        <h2 className="flex items-center gap-2 font-nunito text-lg font-bold tracking-tight text-[#2D3748]"><GraduationCap className="h-5 w-5 text-[#FF7E67]" /> Assignments</h2>
        <div className="mt-4 space-y-2">
          {d.assignments.map((x) => (
            <button key={x.id} onClick={() => act(() => api.post(`/student/assignments/${x.id}/complete`))}
              className="flex w-full items-center gap-3 rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 py-3 text-left hover:border-[#FF7E67]">
              <Circle className="h-5 w-5 text-[#A0AEC0]" />
              <span className="flex-1 text-sm text-[#2D3748]">{x.title} {x.course && <span className="text-[#718096]">• {x.course}</span>}</span>
              {x.due_date && <span className="text-xs text-[#FF7E67]">{x.due_date}</span>}
            </button>
          ))}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
            <input value={a.title} onChange={(e) => setA({ ...a, title: e.target.value })} placeholder="Assignment" data-testid="assignment-title"
              className="h-10 rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 text-sm text-[#2D3748] outline-none focus:border-[#FF7E67] sm:col-span-2" />
            <input value={a.course} onChange={(e) => setA({ ...a, course: e.target.value })} placeholder="Course"
              className="h-10 rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 text-sm text-[#2D3748] outline-none focus:border-[#FF7E67]" />
            <input value={a.due_date} onChange={(e) => setA({ ...a, due_date: e.target.value })} type="date"
              className="h-10 rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 text-sm text-[#2D3748] outline-none focus:border-[#FF7E67]" />
          </div>
          <button onClick={() => a.title && act(() => api.post("/student/assignments", a).then(() => setA({ title: "", course: "", due_date: "" })))} data-testid="add-assignment-btn"
            className="flex items-center gap-2 rounded-full bg-sunrise px-4 py-2 text-sm font-bold text-[#2D3748]"><Plus className="h-4 w-4" /> Add</button>
        </div>
      </div>

      <div className="rounded-3xl border border-[#FFE8E3] bg-white p-6">
        <h2 className="flex items-center gap-2 font-nunito text-lg font-bold tracking-tight text-[#2D3748]"><Briefcase className="h-5 w-5 text-[#FF7E67]" /> Internship Tracker</h2>
        <div className="mt-4 space-y-2">
          {d.internships.map((x) => (
            <div key={x.id} className="flex items-center justify-between rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 py-3">
              <span className="text-sm text-[#2D3748]">{x.role} <span className="text-[#718096]">@ {x.company}</span></span>
              <select value={x.status} onChange={(e) => act(() => api.put(`/student/internships/${x.id}`, { status: e.target.value }))}
                className="rounded-lg border border-[#FFE8E3] bg-white px-2 py-1 text-xs capitalize text-[#FF7E67] outline-none">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          ))}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <input value={i.company} onChange={(e) => setI({ ...i, company: e.target.value })} placeholder="Company" data-testid="internship-company"
              className="h-10 rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 text-sm text-[#2D3748] outline-none focus:border-[#FF7E67]" />
            <input value={i.role} onChange={(e) => setI({ ...i, role: e.target.value })} placeholder="Role"
              className="h-10 rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 text-sm text-[#2D3748] outline-none focus:border-[#FF7E67]" />
          </div>
          <button onClick={() => i.company && i.role && act(() => api.post("/student/internships", i).then(() => setI({ company: "", role: "", status: "applied" })))} data-testid="add-internship-btn"
            className="flex items-center gap-2 rounded-full bg-sunrise px-4 py-2 text-sm font-bold text-[#2D3748]"><Plus className="h-4 w-4" /> Track Application</button>
        </div>
      </div>
    </div>
  );
}
